import { hasTag } from "../app/guards.js";
import { isURL } from "../utils.js";
import { ResourceTiming, PageLoadTiming, PageRenderTiming } from "../common/messages.js";

function getPaintBlocks(resources) {
  var paintBlocks = [];
  var elements = document.getElementsByTagName('*');
  var styleURL = /url\(("[^"]*"|'[^']*'|[^)]*)\)/i;

  for (var i = 0; i < elements.length; i++) {
    var element = elements[i];
    var src = '';

    if (hasTag(element, "IMG")) {
      src = element.currentSrc || element.src;
    }

    if (!src) {
      var backgroundImage = getComputedStyle(element).getPropertyValue('background-image');

      if (backgroundImage) {
        var matches = styleURL.exec(backgroundImage);

        if (matches !== null) {
          src = matches[1];

          if (src.startsWith('"') || src.startsWith("'")) {
            src = src.substr(1, src.length - 2);
          }
        }
      }
    }

    if (!src) continue;
    var time = src.substr(0, 10) === 'data:image' ? 0 : resources[src];
    if (time === undefined) continue;
    var rect = element.getBoundingClientRect();
    var top = Math.max(rect.top, 0);
    var left = Math.max(rect.left, 0);
    var bottom = Math.min(rect.bottom, window.innerHeight || document.documentElement && document.documentElement.clientHeight || 0);
    var right = Math.min(rect.right, window.innerWidth || document.documentElement && document.documentElement.clientWidth || 0);
    if (bottom <= top || right <= left) continue;
    var area = (bottom - top) * (right - left);
    paintBlocks.push({
      time: time,
      area: area
    });
  }

  return paintBlocks;
}

function calculateSpeedIndex(firstContentfulPaint, paintBlocks) {
  var a = Math.max(document.documentElement && document.documentElement.clientWidth || 0, window.innerWidth || 0) * Math.max(document.documentElement && document.documentElement.clientHeight || 0, window.innerHeight || 0) / 10;
  var s = a * firstContentfulPaint;

  for (var i = 0; i < paintBlocks.length; i++) {
    var _paintBlocks$i = paintBlocks[i],
        time = _paintBlocks$i.time,
        area = _paintBlocks$i.area;
    a += area;
    s += area * (time > firstContentfulPaint ? time : firstContentfulPaint);
  }

  return a === 0 ? 0 : s / a;
}

export default function (app, opts) {
  var options = Object.assign({
    captureResourceTimings: true,
    capturePageLoadTimings: true,
    capturePageRenderTimings: true
  }, opts);

  if (!('PerformanceObserver' in window)) {
    options.captureResourceTimings = false;
  }

  if (!options.captureResourceTimings) {
    return;
  } // Resources are necessary for all timings


  var resources = {};

  function resourceTiming(entry) {
    if (entry.duration < 0 || !isURL(entry.name) || app.isServiceURL(entry.name)) return;

    if (resources !== null) {
      resources[entry.name] = entry.startTime + entry.duration;
    }

    app.send(new ResourceTiming(entry.startTime + performance.timing.navigationStart, entry.duration, entry.responseStart && entry.startTime ? entry.responseStart - entry.startTime : 0, entry.transferSize > entry.encodedBodySize ? entry.transferSize - entry.encodedBodySize : 0, entry.encodedBodySize || 0, entry.decodedBodySize || 0, entry.name, entry.initiatorType));
  }

  var observer = new PerformanceObserver(function (list) {
    return list.getEntries().forEach(resourceTiming);
  });
  var prevSessionID;
  app.attachStartCallback(function (_ref) {
    var sessionID = _ref.sessionID;

    if (sessionID !== prevSessionID) {
      // Send past page resources on a newly started session
      performance.getEntriesByType('resource').forEach(resourceTiming);
      prevSessionID = sessionID;
    }

    observer.observe({
      entryTypes: ['resource']
    });
  });
  app.attachStopCallback(function () {
    observer.disconnect();
  });
  var firstPaint = 0,
      firstContentfulPaint = 0;

  if (options.capturePageLoadTimings) {
    var pageLoadTimingSent = false;
    app.ticker.attach(function () {
      if (pageLoadTimingSent) {
        return;
      }

      if (firstPaint === 0 || firstContentfulPaint === 0) {
        performance.getEntriesByType('paint').forEach(function (entry) {
          var name = entry.name,
              startTime = entry.startTime;

          switch (name) {
            case 'first-paint':
              firstPaint = startTime;
              break;

            case 'first-contentful-paint':
              firstContentfulPaint = startTime;
              break;
          }
        });
      }

      if (performance.timing.loadEventEnd || performance.now() > 30000) {
        pageLoadTimingSent = true;
        var _performance$timing = performance.timing,
            navigationStart = _performance$timing.navigationStart,
            requestStart = _performance$timing.requestStart,
            responseStart = _performance$timing.responseStart,
            responseEnd = _performance$timing.responseEnd,
            domContentLoadedEventStart = _performance$timing.domContentLoadedEventStart,
            domContentLoadedEventEnd = _performance$timing.domContentLoadedEventEnd,
            loadEventStart = _performance$timing.loadEventStart,
            loadEventEnd = _performance$timing.loadEventEnd;
        app.send(new PageLoadTiming(requestStart - navigationStart || 0, responseStart - navigationStart || 0, responseEnd - navigationStart || 0, domContentLoadedEventStart - navigationStart || 0, domContentLoadedEventEnd - navigationStart || 0, loadEventStart - navigationStart || 0, loadEventEnd - navigationStart || 0, firstPaint, firstContentfulPaint));
      }
    }, 30);
  }

  if (options.capturePageRenderTimings) {
    var visuallyComplete = 0,
        interactiveWindowStartTime = 0,
        interactiveWindowTickTime = 0,
        paintBlocks = null;
    var pageRenderTimingSent = false;
    app.ticker.attach(function () {
      if (pageRenderTimingSent) {
        return;
      }

      var time = performance.now();

      if (resources !== null) {
        visuallyComplete = Math.max.apply(null, Object.keys(resources).map(function (k) {
          return resources[k];
        }));

        if (time - visuallyComplete > 1000) {
          paintBlocks = getPaintBlocks(resources);
          resources = null;
        }
      }

      if (interactiveWindowTickTime !== null) {
        if (time - interactiveWindowTickTime > 50) {
          interactiveWindowStartTime = time;
        }

        interactiveWindowTickTime = time - interactiveWindowStartTime > 5000 ? null : time;
      }

      if (paintBlocks !== null && interactiveWindowTickTime === null || time > 30000) {
        pageRenderTimingSent = true;
        resources = null;
        var speedIndex = paintBlocks === null ? 0 : calculateSpeedIndex(firstContentfulPaint || firstPaint, paintBlocks);
        var timeToInteractive = interactiveWindowTickTime === null ? Math.max(interactiveWindowStartTime, firstContentfulPaint, performance.timing.domContentLoadedEventEnd - performance.timing.navigationStart || 0) : 0;
        app.send(new PageRenderTiming(speedIndex, firstContentfulPaint > visuallyComplete ? firstContentfulPaint : visuallyComplete, timeToInteractive));
      }
    });
  }
}